import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { SettingsProvider } from "@/context/SettingsContext";
import { Toaster } from "@/components/ui/sonner";

import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Onboarding from "@/pages/Onboarding";
import Join from "@/pages/Join";
import Consent from "@/pages/Consent";
import Paywall from "@/pages/Paywall";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import Assessment from "@/pages/Assessment";
import Portrait from "@/pages/Portrait";
import IndividualDashboard from "@/pages/IndividualDashboard";
import OrgDashboard from "@/pages/OrgDashboard";
import AdminPanel from "@/pages/AdminPanel";
import AdminPreview from "@/pages/AdminPreview";
import Landing from "@/pages/Landing";
import SampleOrgReport from "@/pages/SampleOrgReport";
import Glimpse from "@/pages/Glimpse";
import GlimpseQuestions from "@/pages/GlimpseQuestions";
import GlimpsePortrait from "@/pages/GlimpsePortrait";

const Loading = () => (
  <div className="min-h-screen flex items-center justify-center bg-obsidian">
    <div className="cg-eyebrow animate-pulse">Loading…</div>
  </div>
);

function Protected({ roles, children }) {
  const { user, ready } = useAuth();
  const location = useLocation();
  if (!ready || user === null) return <Loading />;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <div className="App min-h-screen bg-obsidian cg-grain">
      <BrowserRouter>
        <SettingsProvider>
        <AuthProvider>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/sample/organization" element={<SampleOrgReport />} />
            <Route path="/sample/individual" element={<SampleOrgReport kind="individual" key="individual" />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/onboarding" element={<Protected roles={["participant"]}><Onboarding /></Protected>} />
            <Route path="/join" element={<Join />} />
            <Route path="/glimpse" element={<Glimpse />} />
            <Route path="/glimpse/questions" element={<Protected roles={["participant"]}><GlimpseQuestions /></Protected>} />
            <Route path="/glimpse/portrait" element={<Protected roles={["participant"]}><GlimpsePortrait /></Protected>} />
            <Route path="/consent" element={<Protected roles={["participant"]}><Consent /></Protected>} />
            <Route path="/payment" element={<Protected roles={["participant"]}><Paywall /></Protected>} />
            <Route path="/payment/success" element={<Protected roles={["participant"]}><PaymentSuccess /></Protected>} />
            <Route path="/payment/cancel" element={<Protected roles={["participant"]}><PaymentCancel /></Protected>} />
            <Route path="/assessment" element={<Protected roles={["participant"]}><Assessment /></Protected>} />
            <Route path="/portrait/:sessionId" element={<Protected roles={["participant"]}><Portrait /></Protected>} />
            <Route path="/dashboard" element={<Protected roles={["participant"]}><IndividualDashboard /></Protected>} />
            <Route path="/org" element={<Protected roles={["org_admin"]}><OrgDashboard /></Protected>} />
            <Route path="/admin" element={<Protected roles={["super_admin"]}><AdminPanel /></Protected>} />
            <Route path="/admin/org/:orgId" element={<Protected roles={["super_admin"]}><OrgDashboard /></Protected>} />
            <Route path="/admin/preview/:version" element={<Protected roles={["super_admin"]}><AdminPreview /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="top-right" theme="dark" />
        </AuthProvider>
        </SettingsProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
